import { useEffect, useRef, useState } from 'react'
import type { AiArticlesResponse } from '../types/news'

const POLL_INTERVAL_MS = 60 * 60 * 1000

function readEmbeddedArticleData(): AiArticlesResponse | null {
  const node = document.getElementById('omikina-article-data')
  if (!node?.textContent) return null
  try {
    const payload = JSON.parse(node.textContent) as AiArticlesResponse
    return Array.isArray(payload.articles) && payload.articles.length > 0 ? payload : null
  } catch {
    return null
  }
}

export interface CompanyArticleState {
  data: AiArticlesResponse | null
  loading: boolean
  error: string | null
}

export function useCompanyArticles(enabled = true): CompanyArticleState {
  const [data, setData] = useState<AiArticlesResponse | null>(() => readEmbeddedArticleData())
  const [loading, setLoading] = useState(enabled && data === null)
  const [error, setError] = useState<string | null>(null)
  const hasImmediateData = useRef(data !== null)

  useEffect(() => {
    if (!enabled) return
    let disposed = false
    let controller: AbortController | null = null
    if (!hasImmediateData.current) queueMicrotask(() => { if (!disposed) setLoading(true) })
    const load = async () => {
      controller?.abort(); controller = new AbortController()
      try {
        const response = await fetch('/api/company-articles', { signal: controller.signal, headers: { Accept: 'application/json' } })
        if (!response.ok) throw new Error(`Article cache returned ${response.status}`)
        const payload = await response.json() as AiArticlesResponse
        if (!disposed) {
          hasImmediateData.current = true
          setData((current) => {
            if (!current) return payload
            const articles = new Map([...current.articles, ...payload.articles].map((article) => [article.id, article]))
            return { ...payload, articles: Array.from(articles.values()).sort((a, b) => new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()) }
          })
          setError(null)
        }
      } catch (cause) {
        if (!disposed && !hasImmediateData.current && (cause as Error).name !== 'AbortError') setError('AI COMPANY UPDATES TEMPORARILY UNAVAILABLE')
      } finally { if (!disposed) setLoading(false) }
    }
    void load()
    const interval = window.setInterval(() => void load(), POLL_INTERVAL_MS)
    return () => { disposed = true; controller?.abort(); window.clearInterval(interval) }
  }, [enabled])

  return { data, loading: enabled && loading, error }
}
